import { isCityCommand } from "../cityCommand/cityVerbs";
import { marksOf } from "../court/general";
import { optionalField } from "../fields";
import { type AxialCoord, hexDistance } from "../hex";
import { structureComplete } from "../land/cities";
import { rankBy } from "../pick";
import { keyedInt } from "../rng";
import { findHex, hexAt, unitAt, unitById } from "../run/board";
import { isCombatTick } from "../run/cadence";
import { seatUnit, soldierSeats } from "../run/seats";
import { hostile, sameOwner, sideOf } from "../run/sides";
import { payDeed, payDeeds } from "../soldier/bounty";
import { isAtWork } from "../soldier/company";
import { holdOf, titleAttack } from "../soldier/valor";
import type {
  Blow,
  BlowLuck,
  FactionId,
  MutableHex,
  RunState,
  SeatState,
  Unit,
} from "../types/state";
import { breaksNow } from "./morale";
import { disbandOrphans } from "./movement";
import { nextStepOf, walledAgainst } from "./routes";
import { unitStats } from "./unit";

/**
 * The combat step: who strikes whom, each blow's size (flank, garrison, entrenchment, veterans), the
 * fight, and the kills credited.
 */

/** A unit that has stood still for a whole move interval and has a hold bonus to show for it (its title's too, V1). */
export function isDugIn(state: RunState, unit: Unit): boolean {
  return holdOf(state, unit) > 0 && state.tick - unit.movedTick >= state.rules.moveIntervalTicks;
}

/** Enemy units this unit can strike: within its range, nearest weakest first (hp, then id). */
export function targetsOf(state: RunState, unit: Unit): Unit[] {
  const range = unitStats(state.rules, unit.kind).range;
  return rankBy(
    state.units.filter(
      (candidate) =>
        hostile(state.rules, candidate.owner, unit.owner) &&
        hexDistance(candidate.coord, unit.coord) <= range,
    ),
    (candidate) => candidate.hp,
    (candidate) => candidate.id,
  );
}

/** Enemy units that can strike this unit from where they stand: strongest first (attack with a seat's title's, V1, then id). */
export function strikersOf(state: RunState, unit: Unit): Unit[] {
  return rankBy(
    state.units.filter(
      (candidate) =>
        hostile(state.rules, candidate.owner, unit.owner) &&
        hexDistance(candidate.coord, unit.coord) <= unitStats(state.rules, candidate.kind).range,
    ),
    (candidate) => -(unitStats(state.rules, candidate.kind).attack + titleAttack(state, candidate)),
    (candidate) => candidate.id,
  );
}

/** Whether any unit of an enemy kingdom could strike a unit of `faction` standing on `coord`. */
export function underFire(state: RunState, faction: FactionId, coord: AxialCoord): boolean {
  return state.units.some(
    (candidate) =>
      hostile(state.rules, candidate.owner, faction) &&
      hexDistance(candidate.coord, coord) <= unitStats(state.rules, candidate.kind).range,
  );
}

/** A unit in a fight: it can strike someone, or someone can strike it. The renderer shows its HP number. */
export function isEngaged(state: RunState, unit: Unit): boolean {
  return targetsOf(state, unit).length > 0 || underFire(state, unit.owner, unit.coord);
}

/**
 * A unit is a threat to a faction while it walks to a hex that faction owns or to one of its
 * units (A27): the renderer shows its road, so an attack can be seen coming and met. An ally's
 * unit is none (K6: it walks through allied land).
 */
export function isThreatTo(state: RunState, unit: Unit, faction: FactionId): boolean {
  if (!hostile(state.rules, unit.owner, faction) || !unit.destination) {
    return false;
  }
  return (
    findHex(state, unit.destination)?.owner === faction ||
    unitAt(state, unit.destination)?.owner === faction
  );
}

/** A unit on an own working city or finished tower is garrisoned (A27). */
export function isGarrisoned(state: RunState, unit: Unit): boolean {
  const hex = findHex(state, unit.coord);
  return (
    hex !== undefined &&
    hex.owner === unit.owner &&
    (hex.structure === "city" || hex.structure === "tower") &&
    structureComplete(hex, state.tick)
  );
}

function garrisonBonus(state: RunState, unit: Unit): number {
  const tactics = state.rules.tactics;
  return tactics && isGarrisoned(state, unit) ? tactics.garrisonBonus : 0;
}

/**
 * A43: a unit on own land within `callRadius` of one of its King's defend banners that has not
 * moved for `entrenchTicks` has dug in. Only by a banner: three banners cap the trenches. In a
 * city-command kingdom (CC1) the ring of its own city's arrow on own land is the banner.
 */
export function isEntrenched(state: RunState, unit: Unit): boolean {
  const tactics = state.rules.tactics;
  if (!tactics || tactics.entrenchBonus <= 0) {
    return false;
  }
  if (state.tick - unit.movedTick < tactics.entrenchTicks) {
    return false;
  }
  if (!sameOwner(findHex(state, unit.coord)?.owner, unit.owner)) {
    return false;
  }
  if (isCityCommand(state, unit.owner)) {
    return holdsArrowRing(state, unit);
  }
  const general = state.rules.court?.general;
  if (!general) {
    return false;
  }
  return marksOf(state, unit.owner).some(
    (mark) => mark.kind === "defend" && hexDistance(mark.target, unit.coord) <= general.callRadius,
  );
}

/** CC1: the unit stands in the ring of its own city's arrow, on land of its kingdom the arrow holds. */
function holdsArrowRing(state: RunState, unit: Unit): boolean {
  const rules = state.rules.cityCommand;
  const city = unit.home === undefined ? undefined : findHex(state, unit.home);
  if (
    !rules ||
    city === undefined ||
    !sameOwner(city.owner, unit.owner) ||
    city.arrow === undefined
  ) {
    return false;
  }
  const target = findHex(state, city.arrow);
  return target?.owner === unit.owner && hexDistance(city.arrow, unit.coord) <= rules.rallyRadius;
}

function entrenchBonus(state: RunState, unit: Unit): number {
  return isEntrenched(state, unit) ? (state.rules.tactics?.entrenchBonus ?? 0) : 0;
}

/** A unit with `tactics.veteranKills` kills or more (ADR-0013 slice 2); never without the tactics block. */
export function isVeteran(state: RunState, unit: Unit): boolean {
  const tactics = state.rules.tactics;
  return tactics !== null && unit.kills >= tactics.veteranKills;
}

function veteranAttack(state: RunState, unit: Unit): number {
  return isVeteran(state, unit) ? (state.rules.tactics?.veteranAttack ?? 0) : 0;
}

/**
 * FL2: the luck of the blow `attackerId` would strike on this state's tick: heavy on one draw in
 * `oneIn`, glancing on another, steady otherwise; never without the luck block. Keyed by the
 * run's seed, the tick and the attacker, so a reader and the fight agree and nothing is spent.
 */
export function luckOf(state: RunState, attackerId: number): BlowLuck | undefined {
  const luck = state.rules.tactics?.luck;
  if (!luck) {
    return undefined;
  }
  const draw = keyedInt(state.seed, state.tick, attackerId, luck.oneIn);
  return draw === 0 ? "heavy" : draw === 1 ? "glancing" : undefined;
}

/**
 * FL2: a blow of `blow` under its luck: `sizePercent` of it (rounded up) more or less, never under
 * 1, as every blow. A floor of 0 kept every average but gave more upsets and shorter fights
 * (the FL2 report).
 */
export function luckyBlow(blow: number, luck: BlowLuck, sizePercent: number): number {
  const swing = Math.ceil((blow * sizePercent) / 100);
  return luck === "heavy" ? blow + swing : Math.max(1, blow - swing);
}

/** Kills credited this step, added onto the units after the blows landed. */
function credit(units: readonly Unit[], kills: ReadonlyMap<number, number>): Unit[] {
  return units.map((unit) => {
    const earned = kills.get(unit.id) ?? 0;
    return earned === 0 ? unit : { ...unit, kills: unit.kills + earned };
  });
}

/** After the blows: the units at 0 HP leave, kills are credited, squads left without a commander dissolve. */
export function settleCasualties(
  units: readonly Unit[],
  kills: ReadonlyMap<number, number>,
): readonly Unit[] {
  return disbandOrphans(
    credit(
      units.filter((unit) => unit.hp > 0),
      kills,
    ),
  );
}

/**
 * The blows the next combat step would land on this state, in attacker id order: the fight's
 * own loop without the mutation (ADR-0013 slice 1). `fight` applies exactly this list, so what
 * the renderer draws before the step is what the step does. FL2: each blow's luck is drawn for
 * the state's tick, so a reader asking ahead names the tick the step will run on.
 */
export function blowsOf(state: RunState): readonly Blow[] {
  const blows: Blow[] = [];
  const struck = new Set<number>();
  for (const unit of state.units) {
    const [target] = targetsOf(state, unit);
    if (!target) {
      continue;
    }
    const hold = isDugIn(state, target) ? holdOf(state, target) : 0;
    const flank = struck.has(target.id) ? (state.rules.tactics?.flankBonus ?? 0) : 0;
    const steady = Math.max(
      1,
      unitStats(state.rules, unit.kind).attack +
        veteranAttack(state, unit) +
        titleAttack(state, unit) +
        flank -
        hold -
        garrisonBonus(state, target) -
        entrenchBonus(state, target),
    );
    // FL2: the luck comes last, so the hold, the flank and the rest still move the odds.
    const luck = luckOf(state, unit.id);
    const blow = luck
      ? luckyBlow(steady, luck, state.rules.tactics?.luck?.sizePercent ?? 0)
      : steady;
    struck.add(target.id);
    blows.push({ attackerId: unit.id, targetId: target.id, blow, ...optionalField("luck", luck) });
  }
  return blows;
}

/**
 * Step 4: on the combat interval every unit with an enemy in range strikes its weakest one
 * (hp, then id) for `attack` less the target's hold bonus when it is dug in, never below 1.
 * Blows land at once, so two units in each other's range trade hits and an archer at two
 * hexes takes none back. A unit with nobody in range whose next step is a walled foreign hex
 * grinds `wallAttack` off it instead. Units at 0 HP are gone. The blow that takes a target to
 * 0, counted in attacker id order, credits its attacker with the kill (ADR-0013 slice 2). A
 * seat's soldier at work earns its pay and valor here, seat by seat in id order (C2, V1): the kills
 * struck together, the kills by its blow while it lives, and the foe its blow leaves to break in
 * the step after (`breakUnits`).
 */
export function fight(state: RunState): RunState {
  if (!isCombatTick(state) || state.units.length === 0) {
    return state;
  }
  const damage = new Map<number, number>();
  const kills = new Map<number, number>();
  const killer = new Map<number, number>();
  const blows = blowsOf(state);
  for (const { attackerId, targetId, blow } of blows) {
    const before = damage.get(targetId) ?? 0;
    damage.set(targetId, before + blow);
    const target = unitById(state, targetId);
    if (target && before < target.hp && before + blow >= target.hp) {
      kills.set(attackerId, (kills.get(attackerId) ?? 0) + 1);
      killer.set(targetId, attackerId);
    }
  }
  const hexes: MutableHex[] = state.hexes.map((hex) => ({ ...hex }));
  for (const unit of state.units) {
    const stats = unitStats(state.rules, unit.kind);
    if (!unit.destination || targetsOf(state, unit).length > 0) {
      continue;
    }
    const side = sideOf(state.rules, unit.owner);
    const next = nextStepOf(state.hexes, side, unit.coord, unit.destination);
    const wall = next === undefined ? undefined : hexAt(hexes, next);
    if (wall && walledAgainst(wall, side)) {
      wall.walls = Math.max(0, wall.walls - stats.wallAttack);
    }
  }
  const units = state.units.map((unit) => {
    const hit = damage.get(unit.id) ?? 0;
    return hit === 0 ? unit : { ...unit, hp: unit.hp - hit };
  });
  const settled: RunState = { ...state, hexes, units: settleCasualties(units, kills) };
  let paid = settled;
  for (const seat of soldierSeats(state)) {
    paid = paySeat(state, settled, paid, seat, { blows, damage, killer });
  }
  return paid;
}

/** One combat step's blows, the damage each target took, and who struck each kill's last blow. */
interface StepBlows {
  readonly blows: readonly Blow[];
  readonly damage: ReadonlyMap<number, number>;
  readonly killer: ReadonlyMap<number, number>;
}

/**
 * What a seat's soldier did this step, paid onto `paid`: C2's kills struck together, by whose
 * blow was last (VC: with its own men they earn no renown), then its own blows (`payStrikes`).
 */
function paySeat(
  state: RunState,
  settled: RunState,
  paid: RunState,
  seat: SeatState,
  step: StepBlows,
): RunState {
  const { blows, damage, killer } = step;
  const together = killsTogether(state, seat, blows, damage);
  const count = (men: boolean, last: boolean): number =>
    together.filter(
      (kill) => kill.withMen === men && (killer.get(kill.targetId) === seat.unitId) === last,
    ).length;
  const shared = payDeeds(
    payDeeds(
      payDeeds(
        payDeeds(paid, seat.id, "kill", count(false, true)),
        seat.id,
        "together",
        count(false, false),
      ),
      seat.id,
      "menKill",
      count(true, true),
    ),
    seat.id,
    "menTogether",
    count(true, false),
  );
  return payStrikes(state, settled, shared, seat, step);
}

/**
 * V1: what a seat's soldier at work did with its own blow this step, paid onto `paid`: each
 * kill while it lives (an enemy veteran's apart), and the foe it struck that breaks now. The
 * break is read on `settled`, the state `breakUnits` reads next, so it is the step's own.
 */
function payStrikes(
  state: RunState,
  settled: RunState,
  paid: RunState,
  seat: SeatState,
  { blows, killer }: StepBlows,
): RunState {
  const soldier = seatUnit(state, seat);
  if (!soldier || !isAtWork(state, soldier) || !unitById(settled, soldier.id)) {
    return paid;
  }
  let next = paid;
  for (const [targetId, attackerId] of killer) {
    const target = unitById(state, targetId);
    if (attackerId === soldier.id && target) {
      next = payDeed(next, seat.id, isVeteran(state, target) ? "veteranSlay" : "slay");
    }
  }
  const struck = blows.find((blow) => blow.attackerId === soldier.id);
  const foe = struck && unitById(settled, struck.targetId);
  return foe && breaksNow(settled, foe) ? payDeed(next, seat.id, "broke") : next;
}

/**
 * Kills struck together (C2): the targets that fall in this combat step on which a seat's
 * soldier, at work, and a comrade both landed a blow, whoever's was the last; `withMen` when one
 * of the comrades was its own man (VC).
 */
function killsTogether(
  state: RunState,
  seat: SeatState,
  blows: readonly Blow[],
  damage: ReadonlyMap<number, number>,
): { readonly targetId: number; readonly withMen: boolean }[] {
  const soldier = seatUnit(state, seat);
  if (!state.rules.company || !soldier || !isAtWork(state, soldier)) {
    return [];
  }
  const mine = blows.filter((blow) => blow.attackerId === soldier.id);
  const shared = mine.filter(({ targetId }) => {
    const target = unitById(state, targetId);
    return (
      target !== undefined &&
      (damage.get(targetId) ?? 0) >= target.hp &&
      blows.some(
        (blow) =>
          blow.targetId === targetId &&
          blow.attackerId !== soldier.id &&
          unitById(state, blow.attackerId)?.owner === soldier.owner,
      )
    );
  });
  return shared.map(({ targetId }) => ({
    targetId,
    withMen: blows.some(
      (blow) =>
        blow.targetId === targetId && unitById(state, blow.attackerId)?.squadOf === soldier.id,
    ),
  }));
}
